import type { ReactElement } from "react";
import { Copy, Eye, MoreVertical, Pencil, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../../../shared/components/ui/dropdown-menu";

interface TitleActionsDropdownProps {
  readonly titleId: string;
  readonly titleName: string;
  readonly onDelete: (titleId: string, titleName: string) => void;
  readonly onDuplicate: (titleId: string) => void;
}

/**
 * Dropdown menu for individual title actions
 * Replaces the "Edit Full" link with a more comprehensive actions menu
 */
export function TitleActionsDropdown({
  titleId,
  titleName,
  onDelete,
  onDuplicate,
}: Readonly<TitleActionsDropdownProps>): ReactElement {
  const navigate = useNavigate();

  const handleEdit = () => {
    navigate(`/admin/titles/${titleId}/edit`);
  };

  const handleView = () => {
    navigate(`/home/title/${titleId}`);
  };

  const handleDuplicate = () => {
    onDuplicate(titleId);
  };

  const handleDelete = () => {
    onDelete(titleId, titleName);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors hover:bg-neutral-800 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-400 disabled:pointer-events-none disabled:opacity-50 h-8 w-8 p-0">
        <MoreVertical className="h-4 w-4" />
        <span className="sr-only">Open menu</span>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[180px]">
        <DropdownMenuItem onClick={handleEdit} className="cursor-pointer gap-2">
          <Pencil className="h-4 w-4" />
          <span>Edit Full</span>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={handleView} className="cursor-pointer gap-2">
          <Eye className="h-4 w-4" />
          <span>View Detail</span>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleDuplicate} className="cursor-pointer gap-2">
          <Copy className="h-4 w-4" />
          <span>Duplicate</span>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={handleDelete}
          className="cursor-pointer gap-2 text-red-400 focus:text-red-300 focus:bg-red-900/20"
        >
          <Trash2 className="h-4 w-4" />
          <span>Delete</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
